import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const AccessibilityContext = createContext(null);

const STORAGE_KEY = "kalpabriksha_a11y_v1";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export function AccessibilityProvider({ children }) {
  const [theme, setTheme] = useState("light");      // "light" | "dark"
  const [fontScale, setFontScale] = useState(1.0);  // 0.8 - 1.4
  const [narrateOn, setNarrateOn] = useState(false);

  // Load saved settings once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.theme) setTheme(saved.theme);
      if (saved.fontScale) setFontScale(saved.fontScale);
      if (typeof saved.narrateOn === "boolean") setNarrateOn(saved.narrateOn);
    } catch {
      // ignore
    }
  }, []);

  // Persist
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ theme, fontScale, narrateOn })
    );
  }, [theme, fontScale, narrateOn]);

  // Apply to DOM (theme + zoom)
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-theme", theme);
    html.style.setProperty("--font-scale", String(fontScale));
  }, [theme, fontScale]);

  const actions = useMemo(() => {
    return {
      theme,
      fontScale,
      narrateOn,

      toggleTheme() {
        setTheme((t) => (t === "dark" ? "light" : "dark"));
      },

      zoomIn() {
        setFontScale((s) => clamp(Number((s + 0.1).toFixed(2)), 0.8, 1.4));
      },
      zoomOut() {
        setFontScale((s) => clamp(Number((s - 0.1).toFixed(2)), 0.8, 1.4));
      },
      resetZoom() {
        setFontScale(1.0);
      },

      toggleNarration() {
        setNarrateOn((x) => !x);
      },
      setNarration(on) {
        setNarrateOn(Boolean(on));
      },
    };
  }, [theme, fontScale, narrateOn]);

  return (
    <AccessibilityContext.Provider value={actions}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useA11y() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useA11y must be used inside AccessibilityProvider");
  return ctx;
}